'use client';

import { createContext, useContext } from 'react';
import { useMe } from "../AuthContext";
import { UserOrganizationRoleEnum } from "../generated/graphql/types";
import { useLibraryCrehanaAuthGetCentralizedDataQuery } from "./get-centralized-data.v5.generated";
import { jsx as _jsx } from "react/jsx-runtime";
var Context = /*#__PURE__*/createContext(undefined);
export var AuthCentralizedProvider = function AuthCentralizedProvider(_ref) {
  var children = _ref.children,
    organizationSlug = _ref.organizationSlug;
  var _useMe = useMe(),
    learningMe = _useMe.me,
    loadingMe = _useMe.loading;
  var _useLibraryCrehanaAut = useLibraryCrehanaAuthGetCentralizedDataQuery({
      skip: loadingMe || !(learningMe !== null && learningMe !== void 0 && learningMe.email) || !organizationSlug,
      variables: {
        // its ok to cast to string because we are skipping the query if email or slug is undefined
        user_email: learningMe === null || learningMe === void 0 ? void 0 : learningMe.email,
        organization_slug: organizationSlug
      },
      context: {
        clientName: 'v5'
      },
      notifyOnNetworkStatusChange: true
    }),
    data = _useLibraryCrehanaAut.data,
    loading = _useLibraryCrehanaAut.loading,
    error = _useLibraryCrehanaAut.error,
    refetch = _useLibraryCrehanaAut.refetch;
  var getSessionData = function getSessionData() {
    if (loading || loadingMe || error || !(data !== null && data !== void 0 && data.get_user_organization)) {
      return undefined;
    }
    var userOrganization = data.get_user_organization;

    // this data will be empty usually when the org slug doesn't exists in our database
    // there is a global layout in the library @crehana/layouts that shows an error page when the org data is null or undefined
    var currentPageOrganization = data.get_organization_by_learning_slug ? {
      id: data.get_organization_by_learning_slug.original_id,
      slug: data.get_organization_by_learning_slug.slug,
      name: data.get_organization_by_learning_slug.name
    } : undefined;
    var sessionData = {
      me: {
        id: userOrganization.user_id,
        role: userOrganization.role,
        helpers: {
          isAdminOrSubAdmin: userOrganization.role === UserOrganizationRoleEnum.Admin || userOrganization.role === UserOrganizationRoleEnum.Subadmin
        },
        organization: {
          id: userOrganization.organization.original_id,
          slug: userOrganization.organization.slug,
          name: userOrganization.organization.name
        },
        user: {
          // do not use the user.id because its the graphql id, not the real db user id
          // id: userOrganization.user.id
          id: userOrganization.user_id.toString(),
          email: userOrganization.user.email,
          firstname: userOrganization.user.firstname,
          fullName: userOrganization.user.full_name,
          lastname: userOrganization.user.lastname,
          createdAt: userOrganization.user.created_at
        }
      },
      currentPageOrganization: currentPageOrganization
    };
    if (learningMe !== null && learningMe !== void 0 && learningMe.isStaff && currentPageOrganization) {
      // If a staff user navigates to pages of another organization,
      // we need to use the organization data based on the current page's slug
      // to prevent queries requiring an organization ID from failing.
      sessionData.me.organization = currentPageOrganization;
    }
    return sessionData;
  };
  var sessionData = getSessionData();
  return /*#__PURE__*/_jsx(Context.Provider, {
    value: {
      me: sessionData === null || sessionData === void 0 ? void 0 : sessionData.me,
      currentPageOrganization: sessionData === null || sessionData === void 0 ? void 0 : sessionData.currentPageOrganization,
      loading: loading || loadingMe,
      error: error,
      refetch: refetch
    },
    children: children
  });
};
export var useCentralizedProviderData = function useCentralizedProviderData() {
  var context = useContext(Context);
  if (!context) {
    throw new Error('useCentralizedMe must be used within a AuthCentralizedProvider');
  }
  return context;
};
export var useCentralizedMe = function useCentralizedMe() {
  var _useCentralizedProvid = useCentralizedProviderData(),
    me = _useCentralizedProvid.me,
    currentPageOrganization = _useCentralizedProvid.currentPageOrganization,
    loading = _useCentralizedProvid.loading,
    error = _useCentralizedProvid.error;
  if (!me) {
    throw new Error('The user must be logged in to use useCentralizedMe. Make sure you are waiting for the query to finish loading before calling this hook.');
  }
  if (!currentPageOrganization) {
    throw new Error("The current organization slug, from the url, doesn't exists. Make sure the url is correct.");
  }
  return {
    me: me,
    currentPageOrganization: currentPageOrganization,
    loading: loading,
    error: error
  };
};